import { useMemo } from "react";
import { Bed } from "lucide-react";

const ISO = Math.PI / 6;
const COS = Math.cos(ISO);
const SIN = Math.sin(ISO);
const WALL_H = 14;
const PLAN_SCALE = 88;

function getMaxPosti(type) {
  const t = (type || "").toLowerCase();
  if (t.includes("tripla")) return 3;
  if (t.includes("doppia")) return 2;
  return 1;
}

function roomStatus(room) {
  if (!room) return { floor: "#e2e8f0", wallL: "#94a3b8", wallR: "#64748b", top: "#f8fafc", label: "—" };
  const maxP = getMaxPosti(room.type);
  const liberi = room.posti_liberi ?? 0;
  const occupati = Math.max(0, maxP - liberi);
  if (occupati === 0) {
    return { floor: "#a7f3d0", wallL: "#34d399", wallR: "#059669", top: "#ecfdf5", label: "Libera" };
  }
  if (liberi === 0) {
    return { floor: "#fecdd3", wallL: "#fb7185", wallR: "#e11d48", top: "#fff1f2", label: "Piena" };
  }
  return { floor: "#fde68a", wallL: "#fbbf24", wallR: "#d97706", top: "#fffbeb", label: "Parziale" };
}

function toIso(x, y, z = 0) {
  return {
    x: (x - y) * COS,
    y: (x + y) * SIN - z,
  };
}

function guestInitials(nome, cognome) {
  const a = (nome || "").trim().charAt(0);
  const b = (cognome || "").trim().charAt(0);
  return (a + b).toUpperCase() || "?";
}

/** Vertici pavimento stanza (z=0) in coordinate plan 0..PLAN_SCALE */
function roomFootprint(zone) {
  const x0 = zone.x * PLAN_SCALE;
  const y0 = zone.y * PLAN_SCALE;
  const x1 = (zone.x + zone.w) * PLAN_SCALE;
  const y1 = (zone.y + zone.h) * PLAN_SCALE;
  return [
    toIso(x0, y0, 0),
    toIso(x1, y0, 0),
    toIso(x1, y1, 0),
    toIso(x0, y1, 0),
  ];
}

function wallFace(p0, p1, z0, z1) {
  const a = toIso(p0.x, p0.y, z0);
  const b = toIso(p1.x, p1.y, z0);
  const c = toIso(p1.x, p1.y, z1);
  const d = toIso(p0.x, p0.y, z1);
  return `${a.x},${a.y} ${b.x},${b.y} ${c.x},${c.y} ${d.x},${d.y}`;
}

function RoomBlock({ zone, room, onClick, selected }) {
  const st = roomStatus(room);
  const x0 = zone.x * PLAN_SCALE;
  const y0 = zone.y * PLAN_SCALE;
  const x1 = (zone.x + zone.w) * PLAN_SCALE;
  const y1 = (zone.y + zone.h) * PLAN_SCALE;
  const cx = (x0 + x1) / 2;
  const cy = (y0 + y1) / 2;
  const topCenter = toIso(cx, cy, WALL_H + 2);
  const floor = roomFootprint(zone);
  const floorPath = floor.map((p) => `${p.x},${p.y}`).join(" ");

  const topVerts = [
    toIso(x0, y0, WALL_H),
    toIso(x1, y0, WALL_H),
    toIso(x1, y1, WALL_H),
    toIso(x0, y1, WALL_H),
  ];
  const topFace = topVerts.map((p) => `${p.x},${p.y}`).join(" ");

  const wallSouth = wallFace({ x: x0, y: y1 }, { x: x1, y: y1 }, 0, WALL_H);
  const wallEast = wallFace({ x: x1, y: y0 }, { x: x1, y: y1 }, 0, WALL_H);

  const name = (room?.name || zone.label || "Camera").slice(0, 18);
  const ospiti = room?.ospiti || [];
  const labelPos = toIso(cx, cy, WALL_H + 0.5);

  return (
    <g
      className="cursor-pointer transition-opacity hover:opacity-95"
      onClick={(e) => {
        e.stopPropagation();
        onClick(zone);
      }}
      opacity={selected ? 1 : 0.98}
    >
      <polygon points={wallSouth} fill={st.wallR} stroke="#1e293b" strokeWidth={0.25} />
      <polygon points={wallEast} fill={st.wallL} stroke="#1e293b" strokeWidth={0.25} />
      <polygon points={topFace} fill={st.top} stroke="#334155" strokeWidth={0.35} />
      <polygon points={floorPath} fill={st.floor} stroke="#475569" strokeWidth={0.3} opacity={0.95} />

      <text
        x={labelPos.x}
        y={labelPos.y}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="3.2"
        fontWeight="600"
        fill="#0f172a"
        pointerEvents="none"
      >
        {name}
      </text>

      {/* Marker ospiti / posto libero */}
      <g transform={`translate(${topCenter.x}, ${topCenter.y - 4})`} pointerEvents="none">
        {ospiti.length > 0 ? (
          ospiti.slice(0, 3).map((o, i) => (
            <g key={o.id || i} transform={`translate(${(i - (Math.min(ospiti.length, 3) - 1) / 2) * 7}, 0)`}>
              <circle r="4.2" fill="#ffffff" stroke="#059669" strokeWidth="0.6" />
              <text
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="3"
                fontWeight="700"
                fill="#047857"
              >
                {guestInitials(o.nome, o.cognome)}
              </text>
            </g>
          ))
        ) : (
          <g>
            <circle r="4.5" fill="#f8fafc" stroke="#94a3b8" strokeWidth="0.5" />
            <text textAnchor="middle" dominantBaseline="middle" fontSize="2.8" fill="#64748b">
              ∅
            </text>
          </g>
        )}
      </g>

      {selected && (
        <polygon points={topFace} fill="none" stroke="#0f172a" strokeWidth="0.8" />
      )}
    </g>
  );
}

/**
 * Vista isometrica 3D (2.5D) delle stanze — stile dashboard domotica.
 * Usa le zone tracciate sulla planimetria caricata o generate.
 */
export default function IsometricPlanView({
  zones,
  roomsById,
  pianoLabel,
  planImageUrl,
  selectedZoneId,
  onZoneClick,
}) {
  const sortedZones = useMemo(
    () =>
      [...zones].sort(
        (a, b) =>
          b.x + b.y + b.w + b.h - (a.x + a.y + a.w + a.h) ||
          a.x - b.x
      ),
    [zones]
  );

  const viewBox = useMemo(() => {
    if (!zones.length) return "-10 -10 120 90";
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    zones.forEach((z) => {
      const pts = roomFootprint(z);
      pts.forEach((p) => {
        const top = toIso(
          (z.x + z.w / 2) * PLAN_SCALE,
          (z.y + z.h / 2) * PLAN_SCALE,
          WALL_H + 8
        );
        [p, top].forEach((pt) => {
          minX = Math.min(minX, pt.x);
          minY = Math.min(minY, pt.y);
          maxX = Math.max(maxX, pt.x);
          maxY = Math.max(maxY, pt.y);
        });
      });
    });
    const pad = 8;
    return `${minX - pad} ${minY - pad} ${maxX - minX + pad * 2} ${maxY - minY + pad * 2}`;
  }, [zones]);

  return (
    <div
      className="relative w-full rounded-xl overflow-hidden border border-slate-300 bg-gradient-to-b from-slate-100 to-slate-200 shadow-inner"
      style={{ aspectRatio: "16 / 10", maxHeight: "min(72vh, 760px)" }}
      data-testid="planimetria-3d-view"
    >
      {planImageUrl && (
        <img
          src={planImageUrl}
          alt=""
          className="absolute inset-0 w-full h-full object-contain opacity-[0.12] pointer-events-none mix-blend-multiply"
          draggable={false}
        />
      )}
      <svg className="absolute inset-0 w-full h-full" viewBox={viewBox} preserveAspectRatio="xMidYMid meet">
        {/* Pavimento base edificio */}
        <polygon
          points={`${toIso(0, 0).x},${toIso(0, 0).y} ${toIso(PLAN_SCALE, 0).x},${toIso(PLAN_SCALE, 0).y} ${toIso(PLAN_SCALE, PLAN_SCALE * 0.625).x},${toIso(PLAN_SCALE, PLAN_SCALE * 0.625).y} ${toIso(0, PLAN_SCALE * 0.625).x},${toIso(0, PLAN_SCALE * 0.625).y}`}
          fill="#cbd5e1"
          stroke="#94a3b8"
          strokeWidth="0.4"
        />
        {sortedZones.map((zone) => (
          <RoomBlock
            key={zone.id}
            zone={zone}
            room={zone.room_id ? roomsById[zone.room_id] : null}
            selected={selectedZoneId === zone.id}
            onClick={onZoneClick}
          />
        ))}
      </svg>

      <div className="absolute bottom-3 left-3 right-3 flex flex-wrap items-center justify-between gap-2 pointer-events-none">
        <p className="text-xs text-slate-600 bg-white/80 backdrop-blur px-2 py-1 rounded-md border border-slate-200">
          {pianoLabel} — vista 3D isometrica
        </p>
        <div className="flex items-center gap-3 text-[10px] text-slate-600 bg-white/80 backdrop-blur px-2 py-1 rounded-md border border-slate-200">
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-emerald-400 ring-1 ring-emerald-600" /> Ospite
          </span>
          <span className="inline-flex items-center gap-1">
            <Bed className="w-3 h-3" /> Libera
          </span>
        </div>
      </div>
    </div>
  );
}
